import React from 'react';
import { cn } from '@/lib/utils';

export interface LadderStep {
  name: string;
  rate: number;
}

interface OvertimeLadderVisualProps {
  ladder: LadderStep[];
  extendedTiers?: LadderStep[];
}

export const ChevronDown: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 0v8M3 5l3 3 3-3" />
  </svg>
);

const TierCard: React.FC<{ step: LadderStep; badge: string; extended?: boolean }> = ({ step, badge, extended }) => (
  <div
    className={cn(
      'w-full grid grid-cols-[1fr_minmax(0,2fr)_1fr] items-center gap-2 px-2 py-2 rounded',
      'border bg-surface-2 dark:bg-transparent',
      extended
        ? 'border-dashed border-primary/25 bg-primary/[0.03]'
        : 'border-border/60 [border-top-color:rgba(255,255,255,0.12)]',
      'shadow-[inset_0_1px_2px_rgba(0,0,0,0.12)]'
    )}
  >
    <span className={cn(
      'justify-self-start font-mono text-xs font-bold tabular-nums',
      extended ? 'text-primary-text/70' : 'text-primary-text'
    )}>
      {step.rate}x
    </span>
    <span className={cn(
      'block w-full text-center font-heading text-[10px] sm:text-[12px] uppercase tracking-wide truncate',
      extended ? 'text-primary-text/70' : 'text-foreground/80'
    )}>
      {step.name}
    </span>
    <span className={cn(
      'justify-self-end inline-flex items-center rounded px-2 py-1 text-[10px] sm:text-[12px] font-mono font-semibold leading-none',
      extended
        ? 'border border-dashed border-primary/25 bg-primary/[0.06] text-primary-text/55'
        : 'bg-primary/15 text-primary-text'
    )}>
      {badge}
    </span>
  </div>
);

/**
 * Read-only visual representation of the overtime ladder + extended tiers.
 * Designed for injection into help-sheet markdown via `injectableComponents`.
 */
export const OvertimeLadderVisual: React.FC<OvertimeLadderVisualProps> = ({ ladder, extendedTiers }) => {
  if (ladder.length === 0) return null;

  return (
    <div className="grid gap-0 my-1 w-full">
      {ladder.map((step, idx) => (
        <React.Fragment key={step.name}>
          {idx > 0 && (
            <div className="grid place-items-center pt-2 pb-1">
              <ChevronDown className="text-primary-text/70" />
            </div>
          )}
          <TierCard
            step={step}
            badge={idx === 0 ? 'Base' : `Tier ${idx}`}
          />
        </React.Fragment>
      ))}

      {extendedTiers && extendedTiers.length > 0 && (
        <>
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 my-2">
            <div className="h-px bg-primary/35" />
            <span className="font-mono text-[10px] sm:text-[12px] uppercase tracking-widest text-primary-text/65">
              Extended
            </span>
            <div className="h-px bg-primary/35" />
          </div>
          {extendedTiers.map((step, idx) => (
            <React.Fragment key={step.name}>
              {idx > 0 && (
                <div className="grid place-items-center pt-2 pb-2">
                  <ChevronDown className="text-primary-text/60" />
                </div>
              )}
              <TierCard
                step={step}
                badge={`Ext ${idx + 1}`}
                extended
              />
            </React.Fragment>
          ))}
        </>
      )}
    </div>
  );
};
